import { getLocaleString } from 'utilities/locales';
import {
  BUNDLE_SQUARE_TIER_BY_BASE_PRODUCT_KEY,
  HARDWARE_BUNDLE_EXPERIMENT_VARIATIONS,
} from 'constants/software';
import { formatTierMonthlyPrice } from 'components/common/receipt/bundle_offer_utils';
import { useGetOrderSummary } from 'typescript/hooks/use_order_actions_client';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import {
  BundleOfferTier,
  BundleOfferTierKey,
} from 'typescript/types/api/solidshop';

// The disclosure mode is chosen from the experiment variation + whether a paid
// Square One (SQ1) line is in the cart — not from the bundle-intent model.
export const SUBSCRIPTION_DISCLOSURE_MODE = Object.freeze({
  // No disclosure (control variant, no SQ1 line, or a $0/Free SQ1 line).
  NONE: 'none',
  // opt_in variant: order-anchored disclaimer, no checkbox ("By placing my
  // order, …").
  OPT_IN: 'opt_in',
  // opt_out variant: required, unchecked-by-default consent checkbox.
  OPT_OUT: 'opt_out',
} as const);

export type SubscriptionDisclosureMode =
  (typeof SUBSCRIPTION_DISCLOSURE_MODE)[keyof typeof SUBSCRIPTION_DISCLOSURE_MODE];

export interface SubscriptionDisclosure {
  mode: SubscriptionDisclosureMode;
  // True only for opt_out: submit must be blocked until consent is given.
  consentRequired: boolean;
  // Formatted monthly price (e.g. "$49"); null when the disclosure is hidden.
  price: string | null;
  hasTrial: boolean;
  trialDays: number;
  // Localized cancel-by date (start + trialDays), client-computed; null when
  // there is no trial. Pre-submit there is no server-side subscription start
  // date, so we template off "today".
  trialEndDate: string | null;
  // Stable identity of the SQ1 subscription terms currently shown. When it
  // changes (line added/removed/switched, price/trial changed) the consent
  // checkbox should reset so stale consent never carries across cart changes.
  consentKey: string;
}

const HIDDEN: SubscriptionDisclosure = {
  mode: SUBSCRIPTION_DISCLOSURE_MODE.NONE,
  consentRequired: false,
  price: null,
  hasTrial: false,
  trialDays: 0,
  trialEndDate: null,
  consentKey: 'none',
};

const MODE_BY_VARIATION: Record<string, SubscriptionDisclosureMode> = {
  [HARDWARE_BUNDLE_EXPERIMENT_VARIATIONS.OPT_IN]:
    SUBSCRIPTION_DISCLOSURE_MODE.OPT_IN,
  [HARDWARE_BUNDLE_EXPERIMENT_VARIATIONS.OPT_OUT]:
    SUBSCRIPTION_DISCLOSURE_MODE.OPT_OUT,
};

const formatTrialEndDate = (trialDays: number): string => {
  const end = new Date();
  end.setDate(end.getDate() + trialDays);
  return new Intl.DateTimeFormat(getLocaleString(), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(end);
};

/**
 * Single source of truth for the disclosure: <SubscriptionDisclosure> and the
 * useSubmitOrder consent gate both derive from here so they never disagree.
 */
export const useSubscriptionDisclosure = (): SubscriptionDisclosure => {
  const { orderNumber } = useCheckoutFlow();
  const { orderSummaryData } = useGetOrderSummary({ orderNumber });

  const offer = orderSummaryData?.hardware_bundle_offer;
  if (!offer) {
    return HIDDEN;
  }

  const mode =
    MODE_BY_VARIATION[offer.experiment_variation ?? ''] ??
    SUBSCRIPTION_DISCLOSURE_MODE.NONE;
  if (mode === SUBSCRIPTION_DISCLOSURE_MODE.NONE) {
    return HIDDEN;
  }

  // Never under-disclose: a cart can transiently hold more than one SQ1 line.
  // Skip $0/Free lines (nothing recurring to disclose); if several paid lines
  // exist, disclose the highest-priced one.
  let disclosed: {
    tierKey: BundleOfferTierKey;
    tier: BundleOfferTier;
    price: string;
  } | null = null;
  for (const item of orderSummaryData?.cart_summary ?? []) {
    const tierKey = BUNDLE_SQUARE_TIER_BY_BASE_PRODUCT_KEY[
      item.base_product_key
    ] as BundleOfferTierKey | undefined;
    const tier = tierKey ? offer.tiers?.[tierKey] : undefined;
    if (!tier?.monthly_cents) continue;
    if (disclosed && tier.monthly_cents <= disclosed.tier.monthly_cents) {
      continue;
    }
    const price = formatTierMonthlyPrice(tier);
    if (price === null) continue;
    disclosed = { tierKey, tier, price };
  }

  if (!disclosed) {
    return HIDDEN;
  }

  const { tierKey, tier, price } = disclosed;

  const trialDays = tier.trial_days ?? 0;
  const hasTrial = trialDays > 0;

  return {
    mode,
    consentRequired: mode === SUBSCRIPTION_DISCLOSURE_MODE.OPT_OUT,
    price,
    hasTrial,
    trialDays,
    trialEndDate: hasTrial ? formatTrialEndDate(trialDays) : null,
    consentKey: `${offer.experiment_variation}:${tierKey}:${tier.monthly_cents}:${trialDays}`,
  };
};
